import { createBreadcrumbsFromAlgoliaproductAndSlugs } from '@/lib/algolia/utils';
import { HOST } from '@/lib/host';
import { createPublicOmniumProduct } from '@/lib/omnium/createPublicProduct';
import {
  MaterializedProductEnrichment,
  productEnrichmentQuery,
} from '@/lib/sanity/queries';
import { configuredSanityClient } from '@/lib/sanity/sanityClient';
import {
  Omnium,
  OmniumInventory,
  OmniumInventoryItemOmniumResult,
} from '@fagmobler/omnium';
import {
  AlgoliaProduct_fagmobler_try,
  Breadcrumbs,
  FagmoblerAlgoliaProduct,
  FagmoblerAlgoliaProductVariant,
  FagmoblerInventory,
  FagmoblerOmniumProduct,
  FagmoblerPublicOmniumProduct,
} from '@fagmobler/types';
import { SearchIndex } from 'algoliasearch';
import {
  GetServerSidePropsContext,
  GetStaticPropsContext,
  PreviewData,
} from 'next';
import { ParsedUrlQuery } from 'querystring';
import { transformOmniumInventoryToFagmoblerInventory } from '@/lib/omnium/transformInventory';
import countries from 'i18n-iso-countries';

countries.registerLocale(require('i18n-iso-countries/langs/no.json'));

export type ProductPageData = {
  publicOmniumProduct: FagmoblerPublicOmniumProduct;
  generatedProductData: {
    originCountry: string | null;
  } | null;
  inventory: FagmoblerInventory | null;
  relatedProducts: FagmoblerAlgoliaProduct[] | null;
  sanityProductEnrichment: MaterializedProductEnrichment | null;
  algoliaProduct: FagmoblerAlgoliaProduct | null;
  algoliaProductVariants: FagmoblerAlgoliaProductVariant[] | null;
  breadcrumbs: Breadcrumbs;
  productsInSerie: FagmoblerAlgoliaProduct[] | null;
  relatedPdfs: any[] | null;
  motherProductSKU: string | null;
};

export const getProductPageData = async ({
  context,
  slugs,
  index,
  categoryType,
}: {
  context: GetStaticPropsContext<ParsedUrlQuery, PreviewData>;
  slugs: string[];
  index: SearchIndex;
  categoryType: 'categories' | 'articleGroup';
}) => {
  const SKU = slugs.at(-1);
  if (
    !SKU ||
    // some GTM script is trying to access this url
    slugs[0] === 'null' ||
    slugs[0] === 'undefined' ||
    slugs[0] === 'sdk'
  ) {
    return null;
  }

  const motherProductSKU = slugs.at(-2);

  const tokenReq = await fetch(
    `${HOST}/api/omnium/token?s=${process.env.VERCEL_SECRET}`
  );
  const tokenRes = (await tokenReq.json()) as { token: string };

  const omnium = new Omnium(tokenRes.token);
  // Check if product exists in Omnium
  // Todo, filter this to be a public omnium product
  const omniumProduct: FagmoblerOmniumProduct | null = await omnium.product
    .getOne(SKU)
    .catch(() => {
      return null;
    });

  if (!omniumProduct) return false;

  const omniumMotherProduct: FagmoblerOmniumProduct =
    motherProductSKU &&
    (await omnium.product.getOne(motherProductSKU).catch(() => {
      return null;
    }));

  const algoliaProduct: FagmoblerAlgoliaProduct | null =
    omniumProduct && (await index.getObject(SKU).catch(() => null));

  if (!algoliaProduct) {
    console.error('No algolia product');
    return null;
  }

  const algoliaMotherProduct: FagmoblerAlgoliaProduct =
    omniumMotherProduct && (await index.getObject(omniumMotherProduct.skuId));

  // Create categories from motherProduct if available
  // This is because products can be published on a product
  // that is not a part of the mother product category
  const { categorySlugs } = algoliaMotherProduct || algoliaProduct;
  const categorySlugsKeys = categorySlugs && Object.keys(categorySlugs);

  const categorymatch =
    categorySlugs &&
    categorySlugsKeys &&
    categorySlugsKeys.find((catKey) => {
      const categorySlug = categorySlugs[catKey];
      const productSlugs = omniumMotherProduct
        ? slugs.slice(0, -2)
        : slugs.slice(0, -1);

      if (categorySlugs && typeof categorySlug === 'string') {
        return productSlugs.join(' > ') === categorySlug;
      }

      if (categorySlugs && typeof categorySlug === 'object') {
        return categorySlug.find((cat) => {
          return productSlugs.join(' > ') === cat;
        });
      }
    });

  // 404 if /rom/<SKU> does not match SKU
  if (!categorymatch && slugs[0] !== SKU) return null;

  const omniumProductInventory:
    | OmniumInventoryItemOmniumResult['result']
    | null = !!omniumProduct
    ? await omnium.inventory
        .getOne(SKU)
        .then((response) => response.result)
        .catch(() => {
          return null;
        })
    : null;

  const inventory = omniumProductInventory
    ? transformOmniumInventoryToFagmoblerInventory(omniumProductInventory)
    : null;

  if (!omniumProduct) {
    console.log('No omnium product');
    return null;
  }

  if (omniumProduct) {
    const algoliaProductVariants: FagmoblerAlgoliaProductVariant[] | null =
      algoliaProduct?.model
        ? await index
            .search<FagmoblerAlgoliaProductVariant>('', {
              filters: `model:"${algoliaProduct.model}"`,
              distinct: false,
              attributesToHighlight: [],
              attributesToRetrieve: [
                'objectID',
                'colour',
                'historicPath',
                'price',
                'campaignPrice',
                'name',
                'image',
                'model',
              ],
              analyticsTags: ['algoliaProductVariants'],
            })
            .then(({ hits }) => hits)
        : null;

    const relatedProducts = (
      await Promise.all(
        omniumProduct.relatedProducts?.map(async (relatedProductCollection) => {
          if (!relatedProductCollection) {
            return { results: [] } as {
              results: FagmoblerAlgoliaProduct[];
            };
          }

          const productIds = relatedProductCollection.relatedProductItems
            ?.map(({ productId }) => productId)
            .filter(Boolean) as string[];

          const products =
            productIds &&
            productIds.length > 0 &&
            (await index.getObjects<FagmoblerAlgoliaProduct>(productIds));

          return products || { results: [] };
        }) || []
      )
    )
      .map((res) => res.results)
      .flat()
      .filter(Boolean) as FagmoblerAlgoliaProduct[] | null;

    const publicOmniumProduct = createPublicOmniumProduct(omniumProduct);

    // const seriePageQuery = groq`*[
    //   _type == "serie" &&
    //   slug.current == $slug
    // ][0] ${serieProjection}
    // `;

    const facetFilters = [`serie:${algoliaProduct.serie}`];

    const productsInSerie = await (
      await index.search('', {
        facetFilters,
        hitsPerPage: 15,
        analyticsTags: ['productsInSerie'],
      })
    ).hits
      .filter(
        (hit) => hit.objectID !== SKU && hit.objectID !== motherProductSKU
      )
      .slice(0, 5);

    const relatedPdfs = await configuredSanityClient
      .fetch(
        `*[_type == "serie" && title match $serie][0] {
            _id,
            "pdfs": *[_type == "pdf" && references(^._id)]{
              ...
            }
          }.pdfs`,
        { serie: algoliaProduct.serie }
      )
      .catch(() => {
        console.error('COuld not fetch PDFs');
        return [];
      });

    const sanityProductEnrichment =
      algoliaProduct &&
      (await configuredSanityClient.fetch<MaterializedProductEnrichment>(
        productEnrichmentQuery,
        { productSKU: algoliaProduct.objectID }
      ));

    const breadcrumbs = createBreadcrumbsFromAlgoliaproductAndSlugs({
      algoliaProduct,
      slugs,
      motherProduct: omniumMotherProduct,
      categoryType,
    });

    const origin =
      publicOmniumProduct.properties.find((prop) => prop.key === 'origin')
        ?.value || false;

    const originCountry = origin && countries.getName(origin, 'no');

    const productPageData: ProductPageData = {
      publicOmniumProduct,
      generatedProductData:
        {
          originCountry: originCountry || null,
        } || null,
      inventory,
      relatedProducts,
      sanityProductEnrichment,
      algoliaProduct,
      algoliaProductVariants,
      breadcrumbs,
      productsInSerie,
      relatedPdfs,
      motherProductSKU: omniumMotherProduct ? motherProductSKU || null : null,
    };

    return productPageData;
  }
};
